import { Div, Text } from "atomize"
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import {
	animate,
	frame,
	motion,
	useMotionValue,
	useMotionValueEvent,
	useTransform,
} from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown, faArrowUp, faCalendarPlus } from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import { theme } from "../settings/config"
import { lessonsContext } from "../providers/lessons.provider"
import { schoolContext } from "../providers/school.provider"
import { profileContext } from "../providers/profile.provider"
import { playersContext } from "../providers/players.provider"
import { modalContext } from "../providers/modal.provider"
import {
	formatElsewhere,
	formatHours,
	getBookedMinutes,
	getDayTitle,
	getLessonOverlaps,
	getWeekDays,
	getWeekRangeLabel,
	groupLessonsElsewhere,
	isElsewhere,
	withLessonsElsewhere,
	workedLessons,
} from "../utils/week.utils"
import LessonsSkeleton from "../atoms/LessonsSkeleton"
import EmptyState from "../atoms/EmptyState"
import MorphingLesson from "../lesson/MorphingLesson"
import { useJustFinished } from "../lesson/JustFinished"
import ElsewhereLessonRow from "./ElsewhereLessonRow"
import MatchRow from "./MatchRow"
import { isMatch } from "../utils/match.utils"
import { teachesInSchool } from "../utils/func.utils"
import {
	MorphLayers,
	VIEW_SWITCH_TRANSITION,
	WEEK_PROGRESS,
	useLerp,
	useMorphMode,
} from "./viewMorph"
import usePullToChangeWeek, { PULL_THRESHOLD } from "./usePullToChangeWeek"

// ──────────────────────────────────────────────────────────────────────────
// LE LEZIONI SUL TELEFONO
//
// Il giorno e la settimana sono la stessa pagina: i sette giorni, di cui il
// giorno scelto è l'unico aperto quando si guarda il giorno. Passando alla
// settimana le sue schede si stringono in righe e gli altri giorni si
// aprono sopra e sotto; tornando, il contrario. Il passaggio lo regola
// l'avanzamento del provider (`viewProgress`, vedi viewMorph), che le dita
// possono tenere a metà.
//
// Mentre gli altri giorni si aprono sopra quello scelto, la pagina scorre
// di quanto crescono: il giorno scelto resta fermo sotto le dita.
//
// Nella settimana ogni lezione è una riga, e toccandola si va al suo
// giorno con la lezione aperta; nel giorno è la scheda di sempre. In mezzo,
// se si è scelto di vederle, ci sono le lezioni negli altri circoli.
//
// Nella settimana, tirando oltre la cima o oltre il fondo si passa alla
// settimana prima o a quella dopo (vedi usePullToChangeWeek).
// ──────────────────────────────────────────────────────────────────────────

// lo spazio sopra ogni giorno della settimana
const DAY_GAP = 20

function PhoneLessons() {
	const { coach, loading: profileLoading } = useContext(profileContext)
	const { loadedSchool } = useContext(playersContext)
	const { getSelectedSchool, selectedSchool, loading: schoolLoading } = useContext(schoolContext)
	const Modal = useContext(modalContext)
	const {
		selectedDate,
		weekLessons,
		elsewhereLessons,
		viewProgress,
		setSelectedDate,
		setLessonsView,
		lessonToOpen,
		setLessonToOpen,
	} = useContext(lessonsContext)
	const mode = useMorphMode(viewProgress)
	const [openLesson, setOpenLesson] = useState(null)

	const days = getWeekDays(selectedDate)
	const monday = days[0]

	// Come nell'elenco del computer: non si chiede se qualcuno sta
	// caricando, si guarda se le prove ci sono (vedi LessonsList).
	const school = getSelectedSchool()
	const isBooting =
		profileLoading ||
		!coach ||
		schoolLoading ||
		(Boolean(school) && (loadedSchool !== selectedSchool || !weekLessons))

	// ── la pagina segue il giorno scelto ──────────────────────────────────
	// Dov'era il giorno scelto sullo schermo quando il passaggio è partito:
	// mentre sopra di lui si aprono o si chiudono gli altri giorni, la pagina
	// scorre quanto serve a tenerlo lì. Si tiene fermo il suo inizio, non le
	// sue lezioni: il titolo del giorno si apre dove c'erano, e arrivati alla
	// settimana si vede di che giorno si tratta (tenendo ferme le lezioni il
	// titolo finiva sotto la fascia in cima). Più precisamente si tiene fermo
	// il titolo, non lo spazio che lo separa dal giorno prima: quello si apre
	// sopra, e il giorno prima resta sotto la fascia invece di spuntare. Se
	// non può (si è già in cima) scivola, e tornando indietro con le dita
	// torna al suo posto.
	const selectedDayRef = useRef(null)
	const anchor = useRef(null)
	const whereIsSelectedDay = () => {
		const element = selectedDayRef.current
		if (!element) return null
		return {
			date: element.dataset.weekDay,
			top:
				element.getBoundingClientRect().top +
				parseFloat(window.getComputedStyle(element).paddingTop || 0),
		}
	}
	// Partendo dal giorno la pagina è corta, e sotto non c'è niente da
	// scorrere: i giorni che si aprono sopra spingerebbero giù le lezioni
	// finché la pagina non si allunga abbastanza. Quanto manca si aggiunge in
	// fondo, vuoto, e si toglie tornati al giorno.
	const roomBelowRef = useRef(null)
	const setRoomBelow = px => {
		if (roomBelowRef.current) roomBelowRef.current.style.height = `${px}px`
	}
	const makeRoomBelow = scroll => {
		const room = document.documentElement.scrollHeight - window.innerHeight - window.scrollY
		if (scroll > room && roomBelowRef.current)
			setRoomBelow(roomBelowRef.current.offsetHeight + scroll - room)
	}
	// Arrivati alla settimana il giorno scelto non va più tenuto fermo, e il
	// posto in più si toglie: dal giorno più in fondo (la domenica) resterebbe
	// sotto mezza pagina vuota. La pagina scivola giù fin dove finirebbe senza
	// (l'ultimo giorno appena sopra la barra in basso), poi il vuoto, ormai
	// fuori vista, va via.
	const settling = useRef(null)
	const shownRoomBelow = () =>
		Math.max(0, window.innerHeight - roomBelowRef.current.getBoundingClientRect().top)
	const settleRoomBelow = () => {
		const room = roomBelowRef.current?.offsetHeight
		if (!room) return
		const bottom = document.documentElement.scrollHeight - room - window.innerHeight
		const target = Math.max(0, Math.min(window.scrollY, bottom))
		// un dito che tocca la pagina la ferma dov'è: del vuoto resta solo
		// quello che si vede, e più in giù non si scorre
		const stopByHand = () => {
			settling.current?.stop()
			if (roomBelowRef.current) setRoomBelow(shownRoomBelow())
		}
		const stopListening = () => {
			document.removeEventListener("touchstart", stopByHand)
			document.removeEventListener("wheel", stopByHand)
		}
		document.addEventListener("touchstart", stopByHand, { passive: true })
		document.addEventListener("wheel", stopByHand, { passive: true })
		const animation = animate(window.scrollY, target, {
			...VIEW_SWITCH_TRANSITION,
			onUpdate: top => window.scrollTo(0, top),
			onComplete: () => {
				stopListening()
				setRoomBelow(0)
			},
		})
		settling.current = {
			stop: () => {
				animation.stop()
				stopListening()
			},
		}
	}
	useEffect(() => () => settling.current?.stop(), [])
	// Riporta le lezioni dov'erano; falso se il giorno scelto è cambiato
	const keepAnchored = () => {
		const now = whereIsSelectedDay()
		if (!now || now.date !== anchor.current?.date) return false
		const delta = now.top - anchor.current.top
		makeRoomBelow(delta)
		window.scrollBy(0, delta)
		return true
	}
	useMotionValueEvent(viewProgress, "change", progress => {
		// se le dita tornano verso il giorno mentre la pagina scivola, la si
		// lascia dov'è
		settling.current?.stop()
		settling.current = null
		// prima che il passo cambi le altezze
		anchor.current ??= whereIsSelectedDay()
		// dopo che gli stili di questo fotogramma sono scritti, prima che si
		// disegni: altezze e scorrimento cambiano insieme
		frame.postRender(() => {
			// allargando su un altro giorno quello scelto cambia: si tiene
			// fermo lui, da dove è
			if (!keepAnchored()) anchor.current = whereIsSelectedDay()
			// fermi a un capo, il prossimo passaggio parte da dove si sarà
			if (progress <= 0 || progress >= 1) anchor.current = null
			// tornati al giorno, il posto in più non serve
			if (progress <= 0) setRoomBelow(0)
			if (progress >= 1) settleRoomBelow()
		})
	})

	// Al primo passo fuori dal giorno gli altri giorni nascono dopo che la
	// pagina ha già scorso per quel passo: appena hanno la loro altezza, prima
	// che si disegnino, le lezioni si rimettono al loro posto. Senza, per un
	// fotogramma sarebbero spinte giù.
	useLayoutEffect(() => {
		if (mode === "moving") frame.postRender(keepAnchored)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode])

	// Aprendo la settimana di oggi si parte da oggi: i giorni passati restano
	// sopra, a portata di dito. Solo quando la settimana compare da sola: se
	// ci si arriva dal giorno, la pagina è già al suo posto.
	// Tirando oltre il bordo, invece, lo scorrimento continua: dalla cima
	// di una settimana si arriva in fondo a quella prima, dal fondo in cima a
	// quella dopo.
	const scrolledToWeek = useRef(null)
	const pulledFrom = useRef(0)
	useLayoutEffect(() => {
		if (isBooting || mode !== "week" || scrolledToWeek.current === monday) return
		const arrivedFromDay = scrolledToWeek.current === "day"
		scrolledToWeek.current = monday
		if (arrivedFromDay) return
		if (pulledFrom.current) {
			window.scrollTo({
				top: pulledFrom.current < 0 ? document.documentElement.scrollHeight : 0,
			})
			pulledFrom.current = 0
			return
		}
		const today = document.getElementById("week_day_today")
		if (today) today.scrollIntoView({ block: "start" })
		else window.scrollTo({ top: 0 })
	}, [isBooting, mode, monday])
	useEffect(() => {
		if (mode !== "week") scrolledToWeek.current = "day"
	}, [mode])

	// ── la lezione aperta ─────────────────────────────────────────────────
	useEffect(() => {
		if (mode !== "day") setOpenLesson(null)
	}, [mode])

	// Dalla settimana si arriva al giorno toccando una lezione: la si trova
	// aperta e sotto gli occhi, come se la si fosse toccata qui.
	const dayLessons = weekLessons?.[selectedDate]
	useEffect(() => {
		if (!lessonToOpen || mode !== "day") return
		if (!dayLessons?.some(lesson => `${lesson.coachID}_${lesson.lessonID}` === lessonToOpen)) return
		setOpenLesson(lessonToOpen)
		setLessonToOpen(null)
		// dopo l'animazione di apertura, altrimenti si scorre verso un punto
		// che si sta ancora spostando
		setTimeout(
			() =>
				document
					.getElementById(`lesson_${lessonToOpen}`)
					?.scrollIntoView({ behavior: "smooth", block: "center" }),
			400,
		)
	}, [lessonToOpen, dayLessons, mode, setLessonToOpen])

	// ── tirare oltre il bordo ─────────────────────────────────────────────
	const { pull, leaving } = usePullToChangeWeek({
		enabled: mode === "week" && !isBooting && !Modal.isOpen,
		week: monday,
		onCommit: direction => {
			pulledFrom.current = direction
			setSelectedDate(
				DateTime.fromISO(selectedDate)[direction > 0 ? "plus" : "minus"]({ weeks: 1 }).toISODate(),
			)
		},
	})
	const contentOpacity = useTransform(leaving, value => 1 - value)

	const goToDay = (date, lesson) => {
		setLessonToOpen(lesson ? `${lesson.coachID}_${lesson.lessonID}` : null)
		setSelectedDate(date)
		setLessonsView("day")
	}

	if (isBooting) return <LessonsSkeleton />

	return (
		<div style={{ position: "relative" }}>
			<PullIndicator side={-1} pull={pull} leaving={leaving} selectedDate={selectedDate} />
			<PullIndicator side={1} pull={pull} leaving={leaving} selectedDate={selectedDate} />
			<motion.div style={{ y: pull, opacity: contentOpacity }}>
				<motion.div
					key={`${selectedSchool}_${monday}`}
					// arrivati tirando, la settimana entra dal lato da cui la si è tirata
					initial={pulledFrom.current ? { y: 40 * pulledFrom.current, opacity: 0 } : { y: 20 }}
					animate={{ y: 0, opacity: 1 }}
					transition={{ ease: [0.04, 0.62, 0.23, 0.98], duration: 0.4 }}>
					{days.map(date =>
						date === selectedDate ? (
							<SelectedDay
								key={`${date}_scelto`}
								dayRef={selectedDayRef}
								date={date}
								mode={mode}
								progress={viewProgress}
								lessons={weekLessons?.[date] ?? []}
								elsewhere={elsewhereLessons[date] ?? []}
								openLesson={openLesson}
								onToggleLesson={id => setOpenLesson(openLesson === id ? null : id)}
								onDayClick={() => goToDay(date)}
								onLessonClick={lesson => goToDay(date, lesson)}
							/>
						) : (
							<OtherDay
								key={date}
								date={date}
								isAbove={date < selectedDate}
								mode={mode}
								progress={viewProgress}
								lessons={weekLessons?.[date] ?? []}
								elsewhere={elsewhereLessons[date] ?? []}
								onDayClick={() => goToDay(date)}
								onLessonClick={lesson => goToDay(date, lesson)}
							/>
						),
					)}
					<div ref={roomBelowRef} aria-hidden="true" />
				</motion.div>
			</motion.div>
		</div>
	)
}

// Nello spazio che si apre tirando oltre il bordo: quale settimana c'è di
// là, e un cerchio che si riempie man mano. Pieno, la freccia si gira e
// dice di lasciare. Sta attaccato alla pagina finché non c'è posto, poi in
// mezzo allo spazio; in cima sotto la fascia, in fondo sopra la barra.
// `side` è -1 in cima (la settimana prima), 1 in fondo (quella dopo).
const RING_RADIUS = 19
const RING_LENGTH = 2 * Math.PI * RING_RADIUS
function PullIndicator({ side, pull, leaving, selectedDate }) {
	const ref = useRef(null)
	const gap = useTransform(pull, value => Math.max(0, -side * value))
	const amount = useTransform(gap, value => Math.min(1, value / PULL_THRESHOLD))
	const opacity = useTransform([amount, leaving], ([a, gone]) => Math.min(1, a * 2) * (1 - gone))
	const ringOffset = useTransform(amount, a => RING_LENGTH * (1 - a))
	const y = useTransform(gap, value => {
		const half = (ref.current?.offsetHeight ?? 0) / 2
		const center = Math.min(value / 2, value - half - 4)
		return side < 0 ? center : -center
	})
	const [isArmed, setArmed] = useState(false)
	useMotionValueEvent(amount, "change", a => setArmed(a >= 1))

	const week = DateTime.fromISO(selectedDate)[side < 0 ? "minus" : "plus"]({ weeks: 1 }).toISODate()

	return (
		<motion.div
			aria-hidden="true"
			style={{
				position: "absolute",
				left: 0,
				right: 0,
				[side < 0 ? "top" : "bottom"]: 0,
				y,
				opacity,
				display: "flex",
				justifyContent: "center",
				pointerEvents: "none",
			}}>
			<div
				ref={ref}
				style={{
					transform: `translateY(${side < 0 ? -50 : 50}%)`,
					display: "flex",
					flexDirection: side < 0 ? "column" : "column-reverse",
					alignItems: "center",
					gap: "0.4rem",
				}}>
				<Div
					pos="relative"
					w="2.75rem"
					h="2.75rem"
					rounded="circle"
					bg={theme.colors.surface}
					shadow={theme.shadows.accent}
					d="flex"
					align="center"
					justify="center">
					<svg
						viewBox="0 0 44 44"
						style={{
							position: "absolute",
							inset: 0,
							width: "100%",
							height: "100%",
							transform: "rotate(-90deg)",
						}}>
						<motion.circle
							cx="22"
							cy="22"
							r={RING_RADIUS}
							fill="none"
							stroke={theme.colors.primary}
							strokeWidth="3"
							strokeLinecap="round"
							strokeDasharray={RING_LENGTH}
							style={{ strokeDashoffset: ringOffset }}
						/>
					</svg>
					<motion.div
						animate={{ rotate: isArmed ? 180 : 0 }}
						transition={{ duration: 0.2 }}
						style={{ display: "flex" }}>
						<FontAwesomeIcon
							icon={side < 0 ? faArrowDown : faArrowUp}
							color={isArmed ? theme.colors.primary : theme.colors.text_light}
						/>
					</motion.div>
				</Div>
				<Text textSize="caption" textAlign="center" style={{ lineHeight: 1.3 }}>
					<Text tag="span" textWeight="600" textColor={theme.colors.gunmetal}>
						{side < 0 ? "Settimana prima" : "Settimana dopo"} · {getWeekRangeLabel(week)}
					</Text>
					<br />
					<Text tag="span" textColor={theme.colors.text_light}>
						{isArmed ? "Rilascia per andarci" : "Tira ancora"}
					</Text>
				</Text>
			</div>
		</motion.div>
	)
}

// Il giorno scelto: nel giorno è l'unico, con le schede; nella settimana è
// un giorno come gli altri, col suo titolo e le righe.
function SelectedDay({
	dayRef,
	date,
	mode,
	progress,
	lessons,
	elsewhere,
	openLesson,
	onToggleLesson,
	onDayClick,
	onLessonClick,
}) {
	const paddingTop = useLerp(progress, 0, DAY_GAP)
	const { isToday } = getDayTitle(date)

	return (
		<motion.div
			ref={dayRef}
			id={isToday ? "week_day_today" : undefined}
			// allargando le dita qui sopra si apre questo giorno
			data-week-day={date}
			// quanto basta per non finire sotto la fascia in cima (header/PhoneHeader)
			style={{ paddingTop, scrollMarginTop: "5rem" }}>
			<MorphLayers
				progress={progress}
				mode={mode}
				week={<DayTitle date={date} lessons={lessons} elsewhere={elsewhere} onClick={onDayClick} />}
			/>
			{/* cambiando giorno le schede entrano come hanno sempre fatto */}
			<motion.div
				key={date}
				initial={mode === "day" ? { y: 20 } : false}
				animate={{ y: 0 }}
				transition={{ ease: [0.04, 0.62, 0.23, 0.98], duration: 0.4 }}>
				<DayLessons
					date={date}
					mode={mode}
					progress={progress}
					lessons={lessons}
					elsewhere={elsewhere}
					openLesson={openLesson}
					onToggleLesson={onToggleLesson}
					onDayClick={onDayClick}
					onLessonClick={onLessonClick}
				/>
			</motion.div>
		</motion.div>
	)
}

// Un altro giorno della settimana: chiuso nel giorno, si apre fino alla sua
// altezza mentre si va verso la settimana.
// I giorni sopra quello scelto crescono verso l'alto, mentre la pagina
// scorre per tenere fermo il giorno scelto: il loro contenuto sta attaccato
// al fondo, cioè al giorno scelto, e si scopre dall'alto come sotto una
// tenda. Attaccato in cima, sul bordo dello schermo si vedrebbe la parte
// bassa che sale, come se le lezioni venissero fuori dal basso.
function OtherDay({
	date,
	isAbove,
	mode,
	progress,
	lessons,
	elsewhere,
	onDayClick,
	onLessonClick,
}) {
	const contentRef = useRef(null)
	const naturalHeight = useMotionValue(0)
	const height = useTransform([progress, naturalHeight], ([t, natural]) => t * natural)
	const { isToday } = getDayTitle(date)

	// a ogni disegno: le righe possono cambiare mentre è aperto
	useLayoutEffect(() => {
		if (!contentRef.current) return
		naturalHeight.set(contentRef.current.offsetHeight)
	})

	const isOpen = mode === "week"
	return (
		<motion.div
			id={isToday ? "week_day_today" : undefined}
			data-week-day={date}
			style={{
				height: isOpen ? "auto" : height,
				opacity: isOpen ? 1 : progress,
				overflow: isOpen ? "visible" : "hidden",
				scrollMarginTop: "5rem",
				display: "flex",
				flexDirection: "column",
				justifyContent: isAbove ? "flex-end" : "flex-start",
			}}>
			{mode !== "day" && (
				<div ref={contentRef} style={{ paddingTop: DAY_GAP, flexShrink: 0 }}>
					<DayTitle date={date} lessons={lessons} elsewhere={elsewhere} onClick={onDayClick} />
					<DayLessons
						date={date}
						mode="week"
						progress={WEEK_PROGRESS}
						lessons={lessons}
						elsewhere={elsewhere}
						onDayClick={onDayClick}
						onLessonClick={onLessonClick}
					/>
				</div>
			)}
		</motion.div>
	)
}

// Il nome del giorno nella settimana, e quante lezioni e ore ha
function DayTitle({ date, lessons, elsewhere, onClick }) {
	const { title, aside, isToday } = getDayTitle(date)
	// "Gioco io" sta in agenda, ma non è una lezione né ore di lavoro
	const worked = workedLessons(lessons)
	const held = worked.length

	// le ore sono quelle del circolo che si guarda; quelle altrove si
	// contano a parte, perché non sono lavoro di qui
	const summary = [
		held > 0 &&
			`${held === 1 ? "1 lezione" : `${held} lezioni`} · ${formatHours(getBookedMinutes(worked))}`,
		formatElsewhere(elsewhere.length),
	]
		.filter(Boolean)
		.join(" · ")

	return (
		<Div
			d="flex"
			justify="space-between"
			align="baseline"
			p={{ x: "0.5rem", b: "0.5rem" }}
			cursor="pointer"
			onClick={onClick}>
			<Text
				textSize="subheader"
				textWeight="bold"
				textColor={isToday ? theme.colors.primary : theme.colors.gunmetal}>
				{title}
				{aside && (
					<Text
						tag="span"
						textWeight="normal"
						textColor={theme.colors.text_light}
						m={{ l: "0.35rem" }}>
						({aside})
					</Text>
				)}
			</Text>
			{summary && (
				<Text
					textSize="caption"
					textColor={theme.colors.text_light}
					style={{ fontVariantNumeric: "tabular-nums" }}>
					{summary}
				</Text>
			)}
		</Div>
	)
}

// Le lezioni di un giorno, con quelle negli altri circoli al loro orario
function DayLessons({
	date,
	mode,
	progress,
	lessons,
	elsewhere,
	openLesson,
	onToggleLesson,
	onDayClick,
	onLessonClick,
}) {
	const { selectedSchool } = useContext(schoolContext)
	const shownLessons = withLessonsElsewhere(lessons, elsewhere)
	const items = groupLessonsElsewhere(shownLessons, selectedSchool)
	// due lezioni che si accavallano, anche con una altrove, lo dicono
	const overlaps = getLessonOverlaps(shownLessons)
	const justFinished = useJustFinished(lessons)

	if (shownLessons.length === 0)
		return (
			<MorphLayers
				progress={progress}
				mode={mode}
				day={<EmptyDay date={date} />}
				week={
					<Div
						p="0.6rem 1rem"
						rounded="15px"
						bg={theme.colors.surface}
						cursor="pointer"
						onClick={onDayClick}>
						<Text textSize="caption" textColor={theme.colors.text_light}>
							Nessuna lezione
						</Text>
					</Div>
				}
			/>
		)

	return items.map(({ lessons: group, joins, index, lesson = group[0] }) =>
		isElsewhere(lesson, selectedSchool) ? (
			<ElsewhereMorph
				key={`${lesson.schoolID}_${lesson.lessonID}`}
				lessons={group}
				progress={progress}
				isFirst={index === 0}
			/>
		) : isMatch(lesson) ? (
			<MatchMorph
				key={lesson.lessonID}
				lesson={lesson}
				progress={progress}
				joins={joins}
				isFirst={index === 0}
			/>
		) : (
			<div key={lesson.lessonID} id={`lesson_${lesson.coachID}_${lesson.lessonID}`}>
				<MorphingLesson
					lesson={lesson}
					progress={progress}
					joins={joins}
					isFirst={index === 0}
					isOpen={openLesson === `${lesson.coachID}_${lesson.lessonID}`}
					onToggleOpen={() => onToggleLesson?.(`${lesson.coachID}_${lesson.lessonID}`)}
					onRowClick={() => onLessonClick(lesson)}
					justFinishedLabel={
						justFinished?.lessonID === lesson.lessonID ? justFinished.label : null
					}
					overlapsWith={overlaps.get(lesson.lessonID)}
				/>
			</div>
		),
	)
}

// Le lezioni in un altro circolo sono righe in tutte e due le viste — quelle
// di seguito in un blocco solo: cambiano solo lo spazio intorno e dove
// cominciano le scritte, con gli stessi margini delle schede (vedi
// MorphingLesson).
function ElsewhereMorph({ lessons, progress, isFirst }) {
	const marginTop = useLerp(progress, 16, isFirst ? 0 : 10)
	const marginBottom = useLerp(progress, 16, 0)
	const paddingX = useLerp(progress, 16, 12)
	return (
		<motion.div style={{ marginTop, marginBottom }}>
			<ElsewhereLessonRow lessons={lessons} paddingX={paddingX} />
		</motion.div>
	)
}

// "Gioco io" è una riga come quelle degli altri circoli, e si muove e si
// attacca come loro
function MatchMorph({ lesson, progress, joins, isFirst }) {
	const mode = useMorphMode(progress)
	const { joinsPrev, joinsNext } = joins
	const marginTop = useLerp(progress, joinsPrev ? 4 : 16, isFirst ? 0 : joinsPrev ? 3 : 10)
	const marginBottom = useLerp(progress, joinsNext ? 4 : 16, 0)
	return (
		<motion.div
			style={{ marginTop, marginBottom }}
			id={`lesson_${lesson.coachID}_${lesson.lessonID}`}>
			<MatchRow lesson={lesson} joins={joins} swipeable={mode !== "moving"} />
		</motion.div>
	)
}

// Un giorno senza lezioni, visto da vicino: chi è appena arrivato va
// accompagnato alla prima, gli altri si possono rilassare.
function EmptyDay({ date }) {
	const { activePlayers } = useContext(playersContext)
	const { getSelectedSchool, selectedSchool } = useContext(schoolContext)
	const { coach } = useContext(profileContext)
	const Modal = useContext(modalContext)

	// chi qui non insegna non ha una prima lezione da segnare
	if (activePlayers.length === 0 && teachesInSchool(coach, selectedSchool))
		return (
			<Div m={{ t: "0.5rem" }}>
				<EmptyState
					icon={faCalendarPlus}
					title="Qui ci vanno le tue lezioni"
					text="Segna la prima: scegli l'ora, chi c'era e come ha pagato. Presenze, incassi e calendario si aggiornano da soli."
					actionText="Aggiungi una lezione"
					color={theme.colors.primary}
					action={() => getSelectedSchool() && Modal.open("newLesson", date)}
				/>
			</Div>
		)

	return (
		<Text textSize="subheader" textAlign="center" p="0.5rem 1rem" m={{ t: "0.5rem" }}>
			RELAX, non hai lezioni in questo giorno! 😌
		</Text>
	)
}

export default PhoneLessons
